import { useState, useEffect } from 'react'
import { Power, Loader2, CheckCircle, XCircle } from 'lucide-react'
import { api } from '../../lib/api'

export default function AdminOnboardingSettings() {
  const [enabled, setEnabled] = useState(true)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const { data } = await api.get('/admin/settings/onboarding_enabled')
      setEnabled(data.value === 'true')
      setError(null)
    } catch (err: any) {
      console.error('Failed to load onboarding settings:', err)
      setError(err?.response?.data?.detail || 'Erreur lors du chargement')
    } finally {
      setLoading(false)
    }
  }

  const handleToggle = async () => {
    try {
      setSaving(true)
      setSuccess(false)
      setError(null)
      
      const newValue = !enabled
      await api.put('/admin/settings/onboarding_enabled', {
        value: newValue ? 'true' : 'false'
      })
      
      setEnabled(newValue)
      setSuccess(true)
      
      // Clear success message after 3s
      setTimeout(() => setSuccess(false), 3000)
    } catch (err: any) {
      console.error('Failed to update onboarding settings:', err)
      setError(err?.response?.data?.detail || 'Erreur lors de la mise à jour')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
          Guide de démarrage
        </h1>
        <p className="text-zinc-600 dark:text-zinc-400">
          Activez ou désactivez le guide de démarrage pour tous les utilisateurs
        </p>
      </div>

      {/* Main Card */}
      <div className="bg-white dark:bg-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-700 shadow-sm overflow-hidden">
        <div className="p-6">
          {/* Toggle Section */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl ${enabled ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-zinc-100 dark:bg-zinc-700'}`}>
                <Power className={`w-6 h-6 ${enabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-zinc-400'}`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">
                  Statut du guide
                </h3>
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  {enabled ? 'Le guide de démarrage est activé' : 'Le guide de démarrage est désactivé'}
                </p>
              </div>
            </div>

            {/* Toggle Switch */}
            <button
              onClick={handleToggle}
              disabled={saving}
              className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 dark:focus:ring-offset-zinc-800 ${
                enabled ? 'bg-emerald-500' : 'bg-zinc-300 dark:bg-zinc-600'
              } ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                  enabled ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Description */}
          <div className="bg-zinc-50 dark:bg-zinc-900/50 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-medium text-zinc-900 dark:text-white mb-2">
              À propos du guide de démarrage
            </h4>
            <ul className="text-sm text-zinc-600 dark:text-zinc-400 space-y-1">
              <li>• Widget interactif avec checklist des étapes</li>
              <li>• Tooltips contextuels sur les pages</li>
              <li>• Lien "Guide de démarrage" dans le menu utilisateur</li>
              <li>• Modal de bienvenue pour les nouveaux utilisateurs</li>
            </ul>
          </div>

          {/* Status Messages */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
              <XCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg text-emerald-700 dark:text-emerald-400">
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">Paramètres mis à jour avec succès</span>
            </div>
          )}
        </div>

        {/* Footer Info */}
        <div className="px-6 py-4 bg-zinc-50 dark:bg-zinc-900/50 border-t border-zinc-200 dark:border-zinc-700">
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            💡 <strong>Note :</strong> Les utilisateurs devront rafraîchir leur page pour voir les changements.
            Le guide peut être réactivé à tout moment.
          </p>
        </div>
      </div>
    </div>
  )
}
